import { StatIcon } from "../assets/gameIcons.js";
import { Icon as FunctionalIcon } from "../ui/Icon.js";
import { useEffect, useState } from "react";
import { useT } from "../i18n/index.js";
import { Panel } from "../ui/Panel.js";
import { Button } from "../ui/Button.js";
import { Timer } from "../ui/Timer.js";
import { useBalances } from "../state/balances.js";
import { useStore } from "./state.js";
import { luckyApi } from "./api.js";
import { BundleList } from "./cards.js";
import type { Bundle } from "./types.js";

function Wheel() {
  const t = useT();
  const lucky = useStore((s) => s.lucky)!;
  const refreshLucky = useStore((s) => s.refreshLucky);
  const run = useStore((s) => s.run);
  const celebrate = useStore((s) => s.celebrate);
  const credits = useBalances((s) => s.balances?.credits ?? 0);
  const [angle, setAngle] = useState(0);
  const [spinning, setSpinning] = useState(false);
  const [last, setLast] = useState<{ segmentId: string; reward: Bundle } | null>(null);
  const n = lucky.wheel.segments.length;
  const spin = async () => {
    if (spinning) return;
    setSpinning(true);
    const res = await run(() => luckyApi.spin());
    if (!res) {
      setSpinning(false);
      return;
    }
    const index = Math.max(0, lucky.wheel.segments.findIndex((s) => s.id === res.segmentId));
    const target = 360 * 5 + (360 - (index * 360) / n - 360 / n / 2);
    setAngle((a) => a - (a % 360) + target);
    window.setTimeout(() => {
      setSpinning(false);
      setLast({ segmentId: res.segmentId, reward: res.reward });
      celebrate({ key: "lucky.wheel", granted: res.reward, doubled: false, vipPoints: 0, nextStep: null });
      void refreshLucky();
    }, 3200);
  };
  const free = lucky.wheel.freeLeft > 0;
  return (
    <div className="lucky-wheel-wrap" data-testid="lucky-wheel">
      <div className="wheel-pointer"><FunctionalIcon name="chevronDown" /></div>
      <div className="wheel" style={{ transform: `rotate(${angle}deg)`, ["--n" as string]: n }}>
        {lucky.wheel.segments.map((s, i) => (
          <div key={s.id} className={`wheel-seg ${s.jackpot ? "jackpot" : ""}`} style={{ transform: `rotate(${(i * 360) / n}deg) skewY(${-(90 - 360 / n)}deg)` }}>
            <span className="wheel-label" style={{ transform: `skewY(${90 - 360 / n}deg) rotate(${180 / n}deg)` }}>
              {s.jackpot ? <StatIcon stat="star" /> : `${s.pct}%`}
            </span>
          </div>
        ))}
      </div>
      <div className="row" style={{ gap: 8, marginTop: 8 }}>
        <span className="badge gold mono">{t("store.lucky.jackpot", { n: lucky.wheel.jackpot })}</span>
        <span className="grow" />
        <Button variant="gold" onClick={() => void spin()} disabled={spinning || (!free && credits < lucky.wheel.price)} data-testid="wheel-spin">
          {free ? t("store.lucky.spinFree") : t("store.lucky.spin", { n: lucky.wheel.price })}
        </Button>
      </div>
      {last ? (
        <div className="fade-in" style={{ marginTop: 8 }} data-testid="wheel-result">
          <BundleList bundle={last.reward} compact />
        </div>
      ) : null}
      <details className="odds">
        <summary>{t("store.lucky.odds")}</summary>
        <ul>
          {lucky.wheel.segments.map((s) => (
            <li key={s.id} className="row">
              <span className="mono" style={{ width: 48 }}>{s.pct}%</span>
              <BundleList bundle={s.reward} compact />
            </li>
          ))}
        </ul>
      </details>
    </div>
  );
}

function Vault() {
  const t = useT();
  const lucky = useStore((s) => s.lucky)!;
  const refreshLucky = useStore((s) => s.refreshLucky);
  const run = useStore((s) => s.run);
  const celebrate = useStore((s) => s.celebrate);
  const credits = useBalances((s) => s.balances?.credits ?? 0);
  const v = lucky.vault;
  const buy = async () => {
    if (await run(() => luckyApi.buyShots(), t("store.lucky.shotsBought", { n: v.shotsPerPurchase }))) void refreshLucky();
  };
  const shoot = async (count: number) => {
    const res = await run(() => luckyApi.shoot(count));
    if (res) {
      celebrate({ key: "lucky.vault", granted: res.granted, doubled: false, vipPoints: 0, nextStep: null });
      void refreshLucky();
    }
  };
  return (
    <div data-testid="lucky-vault">
      <div className="row" style={{ gap: 8, flexWrap: "wrap" }}>
        <span className="badge">{t("store.lucky.round", { n: v.round })}</span>
        {v.treasure ? <span className="badge gold">{t("store.lucky.treasure")}</span> : null}
        <span className="badge mono">{t("store.lucky.shots", { n: v.shots })}</span>
        <span className="dim" style={{ fontSize: 11 }}>{t("store.lucky.grandWithin", { n: v.grandWithin })}</span>
        <span className="grow" />
        <Button onClick={() => void buy()} disabled={credits < v.price} data-testid="vault-buy">
          {t("store.lucky.buyShots", { n: v.shotsPerPurchase, price: v.price })}
        </Button>
      </div>
      <div className="vault-grid">
        {v.tiles.map((tile, i) => (
          <div key={i} className={`vault-tile ${tile ? "open" : ""} ${i === v.revealed ? "next" : ""}`} data-testid={`vault-tile-${i}`}>
            {tile ? <BundleList bundle={tile} compact /> : <span className="dim">?</span>}
          </div>
        ))}
      </div>
      <div className="row" style={{ gap: 8, marginTop: 8 }}>
        <Button variant="gold" onClick={() => void shoot(1)} disabled={v.shots < 1} data-testid="vault-shoot">
          {t("store.lucky.shoot", { n: 1 })}
        </Button>
        <Button onClick={() => void shoot(5)} disabled={v.shots < 5}>
          {t("store.lucky.shoot", { n: 5 })}
        </Button>
      </div>
    </div>
  );
}

function Discount() {
  const t = useT();
  const lucky = useStore((s) => s.lucky)!;
  const refreshLucky = useStore((s) => s.refreshLucky);
  const run = useStore((s) => s.run);
  const celebrate = useStore((s) => s.celebrate);
  const credits = useBalances((s) => s.balances?.credits ?? 0);
  const d = lucky.discount;
  const [coupon, setCoupon] = useState<number | null>(null);
  const draw = async () => {
    const res = await run(() => luckyApi.draw(), undefined);
    if (res) {
      setCoupon(res.index);
      void refreshLucky();
    }
  };
  const buy = async (itemId: string) => {
    if (coupon === null) return;
    const res = await run(() => luckyApi.buyDiscounted(itemId, coupon));
    if (res) {
      celebrate({ key: itemId, granted: res.granted, doubled: false, vipPoints: 0, nextStep: null });
      setCoupon(null);
      void refreshLucky();
    }
  };
  const unused = d.coupons.filter((c) => !c.used);
  const picked = unused.find((c) => c.index === coupon) ?? unused[unused.length - 1] ?? null;
  return (
    <div data-testid="lucky-discount">
      <div className="row" style={{ gap: 8, flexWrap: "wrap" }}>
        <span className="dim" style={{ fontSize: 12 }}>{t("store.lucky.discountHint", { at: d.guaranteedAt.join(" / ") })}</span>
        <span className="grow" />
        <span className="badge mono">{t("store.lucky.draws", { n: d.draws })}</span>
        <Button variant="gold" onClick={() => void draw()} disabled={credits < d.price} data-testid="discount-draw">
          {t("store.lucky.draw", { n: d.price })}
        </Button>
      </div>
      <div className="row" style={{ gap: 6, flexWrap: "wrap", marginTop: 8 }}>
        {unused.map((c) => (
          <button key={c.index} type="button" className={`coupon ${picked?.index === c.index ? "active" : ""}`} onClick={() => setCoupon(c.index)} data-testid={`coupon-${c.index}`}>
            −{c.pct}%
          </button>
        ))}
        {!unused.length ? <span className="dim">{t("store.lucky.noCoupons")}</span> : null}
      </div>
      {d.shopUntil ? (
        <div className="section">
          <h3>
            {t("store.lucky.discountShop")} · <Timer finishesAt={d.shopUntil} />
          </h3>
          <div className="offers">
            {d.shop.map((i) => (
              <article key={i.id} className="offer" data-testid={`discount-${i.id}`}>
                <BundleList bundle={i.reward} compact />
                <div className="row" style={{ marginTop: 6 }}>
                  <span className="mono dim" style={{ textDecoration: "line-through" }}>₵ {i.price}</span>
                  <span className="grow" />
                  <Button variant="primary" disabled={!picked} onClick={() => void buy(i.id)} data-testid={`discount-buy-${i.id}`}>
                    ₵ {picked ? Math.max(1, Math.round(i.price * (1 - picked.pct / 100))) : i.price}
                  </Button>
                </div>
              </article>
            ))}
          </div>
        </div>
      ) : null}
      <details className="odds">
        <summary>{t("store.lucky.odds")}</summary>
        <ul>
          {d.odds.map((o) => (
            <li key={o.pct} className="mono">
              −{o.pct}% · {o.chance}%
            </li>
          ))}
        </ul>
      </details>
    </div>
  );
}

function Capsules() {
  const t = useT();
  const lucky = useStore((s) => s.lucky)!;
  const refreshLucky = useStore((s) => s.refreshLucky);
  const run = useStore((s) => s.run);
  const celebrate = useStore((s) => s.celebrate);
  const credits = useBalances((s) => s.balances?.credits ?? 0);
  const c = lucky.capsule;
  const [last, setLast] = useState<{ capsuleId: string; key: string | null } | null>(null);
  const draw = async () => {
    const res = await run(() => luckyApi.capsule());
    if (res) {
      setLast({ capsuleId: res.capsuleId, key: res.key });
      if (res.reward.items?.length || res.reward.credits) celebrate({ key: res.capsuleId, granted: res.reward, doubled: false, vipPoints: 0, nextStep: null });
      void refreshLucky();
    }
  };
  const open = async (colour: string) => {
    const res = await run(() => luckyApi.openChest(colour));
    if (res) {
      celebrate({ key: `chest.${colour}`, granted: res.granted, doubled: false, vipPoints: 0, nextStep: null });
      void refreshLucky();
    }
  };
  const free = c.freeLeft > 0;
  return (
    <div data-testid="lucky-capsule">
      <div className="row" style={{ gap: 8, flexWrap: "wrap" }}>
        <div className="capsule-machine">
          <span className={`capsule-ball ${last ? "drop" : ""}`} key={last?.capsuleId ?? "idle"} />
        </div>
        <div className="grow">
          <div className="dim" style={{ fontSize: 12 }}>{t("store.lucky.capsuleHint", { n: c.keyMinutes })}</div>
          <div className="row" style={{ gap: 6, marginTop: 6, flexWrap: "wrap" }}>
            {c.keys.map((k, i) => (
              <Button key={`${k.colour}-${i}`} variant="gold" onClick={() => void open(k.colour)} data-testid={`open-chest-${k.colour}`}>
                {t(`store.lucky.chest.${k.colour}`)} · <Timer finishesAt={k.expiresAt} />
              </Button>
            ))}
          </div>
        </div>
        <Button variant="gold" onClick={() => void draw()} disabled={!free && credits < c.price} data-testid="capsule-draw">
          {free ? t("store.lucky.capsuleFree", { n: c.freeLeft }) : t("store.lucky.capsule", { n: c.price })}
        </Button>
      </div>
      <details className="odds">
        <summary>{t("store.lucky.odds")}</summary>
        <ul>
          {c.odds.map((o) => (
            <li key={o.id} className="mono">
              {o.id} · {o.pct}% {o.chest ? `· ${t(`store.lucky.chest.${o.chest}`)}` : ""}
            </li>
          ))}
        </ul>
      </details>
    </div>
  );
}

/** Lucky Wheel, Fortune Vault, Lucky Discount and Capsule Machine with published odds (R56). */
export function LuckyView() {
  const t = useT();
  const lucky = useStore((s) => s.lucky);
  const refreshLucky = useStore((s) => s.refreshLucky);
  useEffect(() => {
    void refreshLucky();
  }, [refreshLucky]);
  if (!lucky) return <p className="hint">{t("common.loading")}</p>;
  return (
    <>
      <Panel title={t("store.lucky.wheel")}>
        <Wheel />
      </Panel>
      <Panel title={t("store.lucky.vault")}>
        <Vault />
      </Panel>
      <Panel title={t("store.lucky.discount")}>
        <Discount />
      </Panel>
      <Panel title={t("store.lucky.capsules")}>
        <Capsules />
      </Panel>
    </>
  );
}
